import { ComponentChildren } from "preact"
import { useState } from "preact/hooks"
import { useDropzone } from "react-dropzone"
import { Button } from "@/components/ui/button"
import { toast } from "@/components/ui/use-toast"
import { FileText, Upload, X } from "lucide-react"

interface ReportUploadPanelProps<T> {
  /** Where the multipart POST goes. The file is sent as `csv_file`. */
  endpoint: string
  /** Extra form fields sent with the file, e.g. `{ integration_id }`. */
  fields?: Record<string, string>
  /** react-dropzone `accept` map. */
  accept: Record<string, string[]>
  /** "Drag and drop the …" line inside the drop area. */
  dropLabel: string
  /** The accepted formats, as shown under the drop label (".csv"). */
  formatsLabel: string
  /** Toast shown on success. Receives the server's JSON so it can say what was in the file. */
  successMessage: (response: T) => { title: string; description: string }
  onUploaded?: (response: T) => void
  /** How to get the report — rendered above the drop area. */
  children?: ComponentChildren
  className?: string
}

export function selected_file_label(file: File | null): string {
  return file ? `${file.name} (${Math.round(file.size / 1024)} KB)` : "No file selected"
}

/**
 * Upload one retailer report that a background sync job then books: instructions, a drop area,
 * and the selected file with Clear / Upload. Shared by every "export this from the portal and give
 * it to us" integration (Walmart, H-E-B).
 */
export function ReportUploadPanel<T = unknown>({
  endpoint,
  fields,
  accept,
  dropLabel,
  formatsLabel,
  successMessage,
  onUploaded,
  children,
  className,
}: ReportUploadPanelProps<T>) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploaded, setUploaded] = useState(false)

  function handleDrop(files: File[]) {
    setSelectedFile(files[0] || null)
    setUploaded(false)
  }

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: handleDrop,
    multiple: false,
    noClick: true,
    noKeyboard: true,
    accept,
  })

  async function handleUpload() {
    if (!selectedFile) return
    setUploading(true)
    try {
      const formData = new FormData()
      Object.entries(fields ?? {}).forEach(([name, value]: [string, string]) => formData.append(name, value))
      formData.append("csv_file", selectedFile)

      const response = await fetch(endpoint, { method: "POST", credentials: "same-origin", body: formData })
      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(upload_error_message(errorText, response.status))
      }
      const data = (await response.json()) as T

      setSelectedFile(null)
      setUploaded(true)
      toast(successMessage(data))
      onUploaded?.(data)
    } catch (error) {
      const message = error instanceof Error ? error.message : "Upload failed"
      toast({ title: "Upload failed", description: message, variant: "destructive" })
    } finally {
      setUploading(false)
    }
  }

  return (
    <div class={`space-y-4 ${className ?? ""}`}>
      {children}

      <div
        {...(getRootProps() as object)}
        class={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          isDragActive ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-gray-400"
        }`}>
        <input {...getInputProps()} />
        <Upload className="mx-auto h-10 w-10 text-gray-400 mb-3" />
        <p class="text-sm font-medium text-gray-900 mb-1">{isDragActive ? "Drop file here" : dropLabel}</p>
        <p class="text-xs text-gray-500 mb-3">{formatsLabel}</p>
        <Button variant="outline" onClick={open} disabled={uploading}>
          Select file
        </Button>
      </div>

      <div class="flex items-center justify-between gap-2 rounded-md border border-gray-200 p-3">
        <div class="flex items-center gap-2 text-sm min-w-0">
          <FileText className="h-4 w-4 text-gray-500 shrink-0" />
          <span class="truncate">{selected_file_label(selectedFile)}</span>
        </div>
        <div class="flex items-center gap-2 shrink-0">
          <Button variant="outline" onClick={() => setSelectedFile(null)} disabled={!selectedFile || uploading}>
            <X className="h-4 w-4 mr-1" />
            Clear
          </Button>
          <Button onClick={handleUpload} disabled={!selectedFile || uploading}>
            {uploading ? "Uploading..." : "Upload & Start Sync"}
          </Button>
        </div>
      </div>

      {uploaded && (
        <p class="text-sm text-green-700">Sync started. It runs in the background and usually takes under a minute.</p>
      )}
    </div>
  )
}

/** The server answers a refused file with `{"code": 400, "message": "…"}`; show the sentence, not the JSON. */
export function upload_error_message(body: string, status: number): string {
  try {
    const parsed = JSON.parse(body) as { error?: unknown; message?: unknown }
    const message = parsed.error ?? parsed.message
    if (typeof message === "string" && message) return message
  } catch {
    // Not JSON — fall through to the raw text.
  }
  return body || `Upload failed (${status})`
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("upload_error_message", () => {
    it("shows the server's sentence rather than its JSON envelope", () => {
      expect(upload_error_message('{"code":400,"message":"This is not H-E-B\'s payment detail export"}', 400)).toBe(
        "This is not H-E-B's payment detail export"
      )
      expect(upload_error_message("plain text", 500)).toBe("plain text")
      expect(upload_error_message("", 502)).toBe("Upload failed (502)")
    })
  })

  describe("selected_file_label", () => {
    it("names the file and its size, or says none is selected", () => {
      expect(selected_file_label(null)).toBe("No file selected")
      expect(selected_file_label(new File([new Uint8Array(2048)], "payment_detail.csv"))).toBe(
        "payment_detail.csv (2 KB)"
      )
    })
  })
}
